import { useEffect, useRef } from 'react';
import { isTabKeepAliveEnabled } from '../config/tabKeepAlive';
import { useTabPaneContext } from '../routing/TabPaneContext';

const formDraftStore = new Map<string, unknown>();

/** Remove rascunhos de formulários associados a um pane (ex.: ao fechar aba). */
export function clearFormDraftsForPanePath(panePath: string): void {
  const prefix = `${panePath}:`;
  Array.from(formDraftStore.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      formDraftStore.delete(key);
    }
  });
}

export interface FormDialogDraft<T> {
  key: string;
  value: T;
  apply: (draft: T) => void;
}

/**
 * Inicializa o formulário apenas ao abrir o dialog (closed→open) ou ao trocar a entidade.
 * Com keep-alive, persiste rascunho entre remounts para não perder texto ao trocar de aba.
 */
export function useFormDialogInit<T = void>(
  open: boolean,
  entityKey: string | number | null | undefined,
  init: () => void,
  draft?: FormDialogDraft<T>,
): void {
  const initRef = useRef(init);
  initRef.current = init;

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const prevOpenRef = useRef(false);
  const lastEntityKeyRef = useRef<string | null>(null);
  const allowPersistRef = useRef(false);

  const persistDrafts = isTabKeepAliveEnabled() && !!draft?.key;

  useEffect(() => {
    if (!persistDrafts || !open || !allowPersistRef.current) return;

    const currentDraft = draftRef.current;
    if (currentDraft?.key) {
      formDraftStore.set(currentDraft.key, currentDraft.value);
    }
  }, [persistDrafts, open, draft?.key, draft?.value]);

  useEffect(() => {
    const entityId = entityKey == null ? 'new' : String(entityKey);
    const currentDraft = draftRef.current;
    const draftKey = currentDraft?.key;

    if (!open) {
      prevOpenRef.current = false;
      allowPersistRef.current = false;
      if (draftKey) {
        formDraftStore.delete(draftKey);
      }
      return;
    }

    const isOpening = !prevOpenRef.current;
    const entityChangedWhileOpen =
      prevOpenRef.current &&
      lastEntityKeyRef.current !== null &&
      lastEntityKeyRef.current !== entityId;

    if (isOpening || entityChangedWhileOpen) {
      const storedDraft =
        persistDrafts && draftKey && formDraftStore.has(draftKey)
          ? (formDraftStore.get(draftKey) as T)
          : undefined;

      if (storedDraft !== undefined && currentDraft?.apply) {
        currentDraft.apply(storedDraft);
      } else {
        initRef.current();
      }

      lastEntityKeyRef.current = entityId;
      allowPersistRef.current = true;
    }

    prevOpenRef.current = true;
  }, [open, entityKey, persistDrafts]);
}

/**
 * Atalho para forms modais: combina useFormDialogInit com rascunho keep-alive por pane.
 */
export function useFormDialogDraft<T>(
  open: boolean,
  entityKey: string | number | null | undefined,
  formName: string,
  init: () => void,
  formData: T,
  applyDraft: (draft: T) => void,
): void {
  const paneContext = useTabPaneContext();

  const draftKey =
    paneContext && isTabKeepAliveEnabled()
      ? `${paneContext.panePath}:${formName}:${entityKey ?? 'new'}`
      : undefined;

  useFormDialogInit(
    open,
    entityKey,
    init,
    draftKey
      ? {
          key: draftKey,
          value: formData,
          apply: applyDraft,
        }
      : undefined,
  );
}
